import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronLeft, ChevronRight, MessageCircle, Minus, Plus, ShieldCheck, Truck, Wallet, X, ZoomIn } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { fcfa, ttc } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/motion";
import ProductCard from "@/components/ProductCard";
import ProductReviews from "@/components/ProductReviews";
import { useSeo } from "@/lib/seo";

const ProductDetail = () => {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  useSeo({
    title: product ? `${product.name} — ${fcfa(ttc(product))}` : "Produit",
    description: product?.description
      ? `${product.description.slice(0, 150)}${product.description.length > 150 ? "…" : ""} Livraison Dakar — Sama-Keur.`
      : "Découvrez ce produit Sama-Keur : livraison à Dakar, paiement Wave, Orange Money ou carte bancaire.",
    path: `/produit/${id}`,
    keywords: product ? `${product.name}, ${product.category} Dakar, acheter ${product.category} Sénégal, meubles Dakar` : "",
    image: product?.image || "",
  });
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setQty(1);
    setImgIdx(0);
    setZoomOpen(false);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        api.get("/products", { params: { category: r.data.category } }).then((res) =>
          setRelated(res.data.filter((p) => p.id !== r.data.id).slice(0, 3))
        );
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const gallery = product ? (product.images?.length ? product.images : [product.image]) : [];
  const prevImg = () => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length);
  const nextImg = () => setImgIdx((i) => (i + 1) % gallery.length);

  // Clavier lightbox : Échap ferme, flèches naviguent
  useEffect(() => {
    if (!zoomOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomOpen(false);
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen, gallery.length]);

  if (loading) {
    return (
      <main className="">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-2 lg:px-10">
          <div className="aspect-[4/5] animate-pulse border border-clay bg-clay/40" />
          <div className="space-y-4 pt-8">
            <div className="h-4 w-32 animate-pulse bg-clay" />
            <div className="h-12 w-3/4 animate-pulse bg-clay" />
            <div className="h-6 w-40 animate-pulse bg-clay" />
          </div>
        </div>
      </main>
    );
  }

  if (notFound || !product) {
    return (
      <main className="">
        <div className="mx-auto max-w-7xl px-6 py-32 text-center lg:px-10">
          <p className="font-display text-3xl text-bark/60">Ce produit n'existe plus.</p>
          <Link to="/boutique" data-testid="product-notfound-back" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-terracotta hover:underline">
            <ArrowLeft className="h-4 w-4" /> Retour à la boutique
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="product-detail-page" className="">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <Link to={`/boutique?categorie=${encodeURIComponent(product.category)}`} data-testid="product-back-link" className="mb-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-bark/50 transition-colors hover:text-terracotta">
          <ArrowLeft className="h-4 w-4" /> {product.category}
        </Link>

        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="pointer-events-none absolute -left-3 -top-3 h-full w-full border border-terracotta/40" />
              <div className="relative aspect-[4/5] overflow-hidden border border-clay bg-cream">
                <img
                  key={gallery[imgIdx]}
                  src={gallery[imgIdx]}
                  alt={`${product.name} — photo ${imgIdx + 1}`}
                  data-testid="product-image-zoom"
                  onClick={() => setZoomOpen(true)}
                  className="h-full w-full cursor-zoom-in object-contain p-4"
                />
                {product.badge && (
                  <span className="absolute left-4 top-4 bg-terracotta px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-sand">
                    {product.badge}
                  </span>
                )}
                {gallery.length > 1 && (
                  <>
                    <button
                      data-testid="product-gallery-prev"
                      onClick={(e) => { e.stopPropagation(); prevImg(); }}
                      className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-sand/80 text-bark backdrop-blur transition-colors hover:bg-terracotta hover:text-sand"
                      aria-label="Photo précédente"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      data-testid="product-gallery-next"
                      onClick={(e) => { e.stopPropagation(); nextImg(); }}
                      className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-sand/80 text-bark backdrop-blur transition-colors hover:bg-terracotta hover:text-sand"
                      aria-label="Photo suivante"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <span data-testid="product-gallery-counter" className="absolute bottom-4 left-4 rounded-full bg-bark/60 px-3 py-1 text-xs font-semibold text-sand backdrop-blur">
                      {imgIdx + 1} / {gallery.length}
                    </span>
                  </>
                )}
                <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-sand/80 text-bark backdrop-blur">
                  <ZoomIn className="h-4 w-4" />
                </span>
              </div>
              {gallery.length > 1 && (
                <div data-testid="product-gallery-thumbs" className="mt-4 grid grid-cols-4 gap-3">
                  {gallery.map((img, idx) => (
                    <button
                      key={img + idx}
                      data-testid={`product-thumb-${idx}`}
                      onClick={() => setImgIdx(idx)}
                      className={`aspect-square overflow-hidden border transition-all duration-300 ${
                        idx === imgIdx ? "border-terracotta shadow-[2px_2px_0_0_#A94833]" : "border-clay opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img src={img} alt={`${product.name} miniature ${idx + 1}`} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-bark/40">Cliquez sur la photo pour l'agrandir.</p>
            </div>
          </Reveal>

          <AnimatePresence>
            {zoomOpen && (
              <motion.div
                data-testid="product-image-lightbox"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setZoomOpen(false)}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-bark/90 p-6 backdrop-blur-sm"
              >
                <motion.img
                  key={imgIdx}
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  src={gallery[imgIdx]}
                  alt={product.name}
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-[88vh] max-w-full border border-clay object-contain"
                />
                {gallery.length > 1 && (
                  <>
                    <button
                      data-testid="lightbox-prev"
                      onClick={(e) => { e.stopPropagation(); prevImg(); }}
                      className="absolute left-6 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-sand/30 text-sand transition-colors hover:border-ochre hover:text-ochre"
                      aria-label="Photo précédente"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      data-testid="lightbox-next"
                      onClick={(e) => { e.stopPropagation(); nextImg(); }}
                      className="absolute right-24 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-sand/30 text-sand transition-colors hover:border-ochre hover:text-ochre sm:right-6"
                      aria-label="Photo suivante"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <span data-testid="lightbox-counter" className="absolute bottom-6 left-6 text-sm font-semibold text-sand/70">
                      {imgIdx + 1} / {gallery.length}
                    </span>
                  </>
                )}
                <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-display text-xl italic text-sand/80">{product.name}</p>
                <button
                  data-testid="product-image-lightbox-close"
                  onClick={() => setZoomOpen(false)}
                  className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-full border border-sand/30 text-sand transition-colors hover:border-ochre hover:text-ochre"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="lg:sticky lg:top-[var(--header-h,8rem)] lg:self-start">
            <Reveal delay={0.1}>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">{product.category}</p>
              <h1 data-testid="product-name" className="mt-3 font-display text-4xl font-bold tracking-tight text-bark sm:text-5xl">
                {product.name}
              </h1>
              <div className="mt-4">
                <p data-testid="product-price" className="font-display text-3xl font-semibold text-terracotta">
                  {fcfa(ttc(product))}
                </p>
                <p data-testid="product-price-ttc-note" className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-bark/45">
                  Prix TTC — tous frais inclus
                </p>
              </div>
              {product.delivery_estimate && (
                <p data-testid="product-delivery-estimate" className="mt-4 inline-flex items-center gap-2 border border-ochre/40 bg-ochre/10 px-4 py-2.5 text-sm text-bark">
                  <Truck className="h-4 w-4 shrink-0 text-terracotta" />
                  Délai de livraison estimé : <span className="font-bold">{product.delivery_estimate}</span>
                </p>
              )}
              <p className="mt-6 max-w-lg leading-relaxed text-bark/70">{product.description}</p>

              {product.features?.length > 0 && (
                <ul className="mt-8 space-y-3 border-t border-clay pt-8">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-bark/70">
                      <Check className="h-4 w-4 shrink-0 text-terracotta" /> {f}
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <div className="flex items-center border border-clay">
                  <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-14 w-12 items-center justify-center text-bark/60 hover:text-terracotta">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="product-qty" className="w-10 text-center font-bold">{qty}</span>
                  <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(20, q + 1))} className="flex h-14 w-12 items-center justify-center text-bark/60 hover:text-terracotta">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <button
                  data-testid="product-add-to-cart"
                  onClick={() => { add(product, qty); toast.success(`${product.name} ajouté au panier`); }}
                  className="group flex h-14 flex-1 items-center justify-center gap-3 rounded-full bg-terracotta px-8 text-sm font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta-dark"
                >
                  Ajouter au panier
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <a
                  data-testid="product-share-whatsapp"
                  href={`https://wa.me/?text=${encodeURIComponent(`Découvrez ce produit chez Sama Keur Shop : ${product.name} — ${fcfa(ttc(product))} TTC (tous frais inclus)\n${window.location.origin}/produit/${product.id}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-14 items-center gap-2 rounded-full border border-[#25D366] px-6 text-xs font-bold uppercase tracking-[0.12em] text-[#1da851] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="h-4 w-4" /> Partager
                </a>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-clay pt-8">
                {[
                  { icon: Truck, label: "Livraison 48h à Dakar" },
                  { icon: Wallet, label: "Wave, OM ou carte" },
                  { icon: ShieldCheck, label: "Garantie produit" },
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <Icon className="h-5 w-5 text-terracotta" />
                    <p className="text-xs font-medium leading-snug text-bark/60">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <ProductReviews productId={id} />

        {related.length > 0 && (
          <div className="mt-28">
            <Reveal>
              <h2 className="mb-10 font-display text-3xl font-semibold tracking-tight text-bark">Vous aimerez aussi</h2>
            </Reveal>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} noZoom />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
